import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// Suppress benign ResizeObserver overlays from CRA dev server (dev only).
// These messages are emitted by Chromium/Radix/shadcn Select popper measurement
// and never affect production behaviour. In dev they trigger the full-screen
// react-error-overlay iframe which blocks user clicks.
if (process.env.NODE_ENV === "development") {
  const RO_MSGS = [
    "ResizeObserver loop completed with undelivered notifications.",
    "ResizeObserver loop limit exceeded",
  ];
  const isRO = (msg) =>
    typeof msg === "string" && RO_MSGS.some((m) => msg.includes(m));

  // 1. Wrap ResizeObserver callbacks in rAF so they never re-enter synchronous
  //    layout inside their own dispatch cycle — this is the root cause of the
  //    "ResizeObserver loop" errors emitted by Radix/shadcn popper measurement.
  //    Well-known workaround; safe for Radix/shadcn Select positioning because
  //    it only defers the callback by one frame.
  if (typeof window.ResizeObserver === "function") {
    const OrigRO = window.ResizeObserver;
    window.ResizeObserver = class DebouncedResizeObserver extends OrigRO {
      constructor(callback) {
        let scheduled = false;
        let lastArgs = null;
        const wrapped = (...args) => {
          lastArgs = args;
          if (scheduled) return;
          scheduled = true;
          window.requestAnimationFrame(() => {
            scheduled = false;
            try {
              callback(...lastArgs);
            } catch (e) {
              /* swallow — RO callback should never crash the app */
            }
          });
        };
        super(wrapped);
      }
    };
  }

  // 2. Belt-and-suspenders: intercept the error event in capture phase in case
  //    a stray RO loop message still slips through (e.g., from third-party code).
  window.addEventListener(
    "error",
    (e) => {
      if (isRO(e.message)) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    },
    true,
  );
  window.addEventListener(
    "unhandledrejection",
    (e) => {
      const m = e?.reason?.message || e?.reason;
      if (isRO(m)) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    },
    true,
  );

  // 3. Swallow console.error calls that are exactly the RO message (leaves all
  //    other errors intact so real bugs still surface).
  const _origError = console.error;
  console.error = (...args) => {
    if (args.length && isRO(args[0])) return;
    _origError(...args);
  };

  // 4. Final safety net: if the CRA dev overlay iframe somehow still appears
  //    with ONLY ResizeObserver errors inside, remove it so it doesn't block
  //    interaction. Real errors (any non-RO content) are left visible.
  const observer = new MutationObserver(() => {
    document.querySelectorAll("iframe").forEach((f) => {
      const isOverlay =
        (f.id && f.id.includes("overlay")) ||
        (f.title && f.title.toLowerCase().includes("overlay"));
      if (!isOverlay) return;
      try {
        const txt = f.contentDocument?.body?.innerText || "";
        if (!txt) return;
        const nonROLines = txt
          .split("\n")
          .filter(
            (line) =>
              line.trim() &&
              !line.includes("ResizeObserver loop") &&
              !line.includes("Uncaught runtime errors") &&
              !line.startsWith("ERROR") &&
              !line.startsWith("at "),
          );
        // If the ONLY error content in the overlay relates to ResizeObserver,
        // remove the whole iframe. Real errors keep the overlay visible.
        if (nonROLines.length === 0 && txt.includes("ResizeObserver")) {
          f.remove();
        }
      } catch {
        /* cross-origin — ignore */
      }
    });
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
